import type { DestinationsContainer } from '@elara/client-core/containers/DestinationsContainer';
import type { HotelsContainer } from '@elara/client-core/containers/HotelsContainer';
import type { PlacesContainer } from '@elara/client-core/containers/PlacesContainer';
import type { PreviewType } from '@elara/client-core/lib/route-utils';
import type { Destination } from '@elara/client-core/models/DestinationModel';
import type { Hotel } from '@elara/client-core/models/HotelModel';
import type { Place } from '@elara/client-core/models/PlaceModel';
import type { DataAttachmentType } from '@elara/domain/models/schemas/attachment-schemas';

import type { ImageGalleryDialogRef } from '#ui/index.js';
import type { IconName } from '#ui/MaterialIcon.js';

import { useRef, useState } from 'react';

import { DATA_ATTACHMENT_ICONS } from '@elara/client-core/constants';
import { shouldLetBrowserHandleClick } from '@elara/client-core/lib/route-utils';
import { cn } from '@elara/client-core/lib/utils';

import { getFileTypeIcon, isImageFile } from '#lib/file-utils.js';
import { useRefreshPlace } from '#lib/use-refresh-place.js';
import { useApp, useRouterState, useTripsState, useUIState } from '#states/hooks.js';
import { DestinationCard } from '#ui/cards/DestinationCard.js';
import { HotelCard } from '#ui/cards/HotelCard.js';
import { PlaceCard } from '#ui/cards/PlaceCard.js';
import { AppImage, Button, Card, IconSizes, ImageGalleryDialog, MaterialIcon, ShortlistButton } from '#ui/index.js';

interface ImageFallbackProps {
  message: string;
}

function ImageFallback({ message }: ImageFallbackProps) {
  return (
    <div className="flex items-center justify-center w-[200px] h-[150px] bg-muted rounded">
      <span className="text-sm text-muted-foreground">{message}</span>
    </div>
  );
}

interface Attachment {
  id: string;
  name: string;
  type: string;
  mimeType?: string;
  size?: number;
  url?: string;
  referenceId?: string;
}

interface MessageAttachmentsProps {
  attachments: Attachment[];
  messageId: string;
}

interface DataAttachmentCardProps {
  type: 'destination' | 'hotel' | 'place';
  referenceId: string;
  name: string;
}

function DataAttachmentCard({ type, referenceId, name }: DataAttachmentCardProps) {
  const tripsState = useTripsState();
  const routerState = useRouterState();

  const { features } = useApp('features');
  const { destinations, hotels, places } = features as {
    destinations: DestinationsContainer;
    hotels: HotelsContainer;
    places: PlacesContainer;
  };

  useRefreshPlace(type === 'place' ? referenceId : undefined);

  const handle =
    type === 'destination'
      ? destinations.ClientDestination.get({ id: referenceId })
      : type === 'hotel'
        ? hotels.ClientHotel.get({ id: referenceId })
        : places.ClientPlace.get({ id: referenceId });

  const canLink = handle.isReady && !handle.error && handle.data;
  const handleClick = (e: React.MouseEvent) => {
    if (!canLink) return;
    if (shouldLetBrowserHandleClick(e)) return;
    e.preventDefault();
    tripsState.showGuide(type as PreviewType, referenceId);
  };

  const href = routerState.buildUrlWithHashParams({ preview: type, id: referenceId });

  const actions = <ShortlistButton itemType={type} referenceId={referenceId} title={name} variant="icon" />;

  const cardBody = (
    <>
      {handle.data && type === 'destination' && (
        <DestinationCard destination={handle.data as Destination} actions={actions} href={href} onClick={handleClick} />
      )}
      {handle.data && type === 'hotel' && (
        <HotelCard hotel={handle.data as Hotel} actions={actions} href={href} onClick={handleClick} />
      )}
      {handle.data && type === 'place' && (
        <PlaceCard place={handle.data as Place} actions={actions} href={href} onClick={handleClick} />
      )}
    </>
  );

  const cardClassName = cn(
    'flex relative border overflow-hidden transition-all max-w-[360px] w-full bg-background',
    canLink && 'hover:shadow-md cursor-pointer',
  );

  return <Card className={cardClassName}>{cardBody}</Card>;
}

export function MessageAttachments({ attachments, messageId }: MessageAttachmentsProps) {
  const uiState = useUIState();

  // DOM refs use null (legacy API)
  const imageGalleryRef = useRef<ImageGalleryDialogRef | undefined>(undefined);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  if (!attachments || attachments.length === 0) return;

  // Get all image attachments for navigation
  const imageAttachments = attachments.filter(
    (attachment) => attachment.url && attachment.mimeType && isImageFile(attachment.mimeType),
  );

  return (
    <div className="flex flex-wrap items-start w-full justify-end gap-2 mb-2">
      {attachments.map((attachment) => {
        // Handle data attachments (destination, hotel, place, etc.)
        if (
          attachment.type === 'destination' ||
          attachment.type === 'hotel' ||
          attachment.type === 'place' ||
          attachment.referenceId
        ) {
          const attachmentType: 'destination' | 'hotel' | 'place' =
            attachment.type === 'destination' || attachment.type === 'hotel' || attachment.type === 'place'
              ? attachment.type
              : 'destination';

          if (attachment.referenceId) {
            return (
              <DataAttachmentCard
                key={attachment.id}
                type={attachmentType}
                referenceId={attachment.referenceId}
                name={attachment.name}
              />
            );
          }

          const icon =
            attachmentType === 'destination' || attachmentType === 'hotel' || attachmentType === 'place'
              ? DATA_ATTACHMENT_ICONS[attachmentType as DataAttachmentType]
              : 'attachment';

          return (
            <Card key={attachment.id} className={cn('overflow-hidden p-2 bg-background-secondary')}>
              <div className="flex items-center gap-2">
                <MaterialIcon icon={icon as IconName} size={IconSizes.sm} className="text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{attachment.name}</p>
                </div>
              </div>
            </Card>
          );
        }

        // Handle media attachments (existing logic)
        const isImage = attachment.mimeType ? isImageFile(attachment.mimeType) : false;
        const { iconName, className: iconClassName } = getFileTypeIcon(attachment.mimeType || '', attachment.name);

        return (
          <Card
            key={attachment.id}
            className={cn('overflow-hidden transition-all hover:shadow-md', isImage ? 'p-0' : 'p-3')}
          >
            {isImage && attachment.url ? (
              // Image preview
              <div className="relative group">
                {failedImages.has(attachment.id) ? (
                  <ImageFallback message="Failed to load image" />
                ) : (
                  <>
                    <AppImage
                      src={attachment.url}
                      alt={attachment.name}
                      className="block max-w-[200px] max-h-[150px] w-auto h-auto object-cover"
                      onError={() => {
                        console.error('[MessageAttachments] Image failed to load:', {
                          url: attachment.url,
                          name: attachment.name,
                        });
                        setFailedImages((prev) => new Set(prev).add(attachment.id));
                      }}
                      onLoad={(e) => {
                        console.debug('[MessageAttachments] Image loaded successfully:', {
                          url: attachment.url,
                          name: attachment.name,
                          width: (e.currentTarget as HTMLImageElement).naturalWidth,
                          height: (e.currentTarget as HTMLImageElement).naturalHeight,
                        });
                      }}
                    />
                    <div
                      className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                      onClick={() => imageGalleryRef.current?.openImage(attachment)}
                    ></div>
                  </>
                )}
              </div>
            ) : (
              // Non-image file display
              <div className="flex items-center gap-3">
                <MaterialIcon icon={iconName} size={IconSizes.lg} className={iconClassName} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{attachment.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {attachment.size && attachment.size > 0
                      ? `${(attachment.size / 1024).toFixed(1)} KB`
                      : 'Unknown size'}
                  </p>
                </div>
                {attachment.url && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')}
                  >
                    <MaterialIcon icon="download" />
                  </Button>
                )}
              </div>
            )}
          </Card>
        );
      })}

      {/* Image Gallery */}
      <ImageGalleryDialog ref={imageGalleryRef} images={imageAttachments} />
    </div>
  );
}
